import {
  isMemberAdmin as isMemberWorkspaceAdmin,
  getMembershipType as getWorkspaceMemberType,
} from '@trello/organizations';
import { ProductFeatures } from '@trello/product-features';
import { Board, Member, BoardMembership } from './types';

type MemberType =
  | 'admin'
  | 'normal'
  | 'observer'
  | 'virtual'
  | 'deactivated'
  | 'unconfirmed'
  | 'org'
  | 'public';

export const getMembershipFor = (
  board: Board,
  memberId: string,
): BoardMembership | undefined => {
  return board.memberships.find(
    (membership) => membership.idMember === memberId,
  );
};

// Disable implicitAdmin if you don't want enterprise admins and workspace
// admins to be automatically counted as board admins if workspace is premium or
// enterprise
export const getMemberType = (
  member: Member,
  board: Board,
  implicitAdmin = true,
): MemberType => {
  if (member.memberType === 'ghost') {
    return 'virtual';
  }

  const workspace = board.organization;

  // If the member is an admin of the board's workspace's enterprise, they are
  // implicitly an admin of the board
  if (implicitAdmin && workspace?.enterprise?.idAdmins?.includes(member.id)) {
    return 'admin';
  }

  // If the member is an admin of the board's workspace, they are implicitly an
  // admin of the board if the workspace is premium
  if (
    implicitAdmin &&
    workspace &&
    ProductFeatures.hasProduct(workspace.products?.[0]) &&
    isMemberWorkspaceAdmin(member, workspace)
  ) {
    return 'admin';
  }

  const boardMembership = getMembershipFor(board, member.id);

  if (boardMembership) {
    if (boardMembership.deactivated) {
      return 'deactivated';
    }

    if (boardMembership.unconfirmed) {
      return 'unconfirmed';
    }

    return boardMembership.memberType;
  }

  // Converted from app/scripts/models/internal/membership-model.js getExplicitMemberType
  if (workspace && getWorkspaceMemberType(member, workspace)) {
    return 'org';
  }

  return 'public';
};

export const isMemberAdmin = (
  member: Member,
  board: Board,
  implicitAdmin = true,
) => {
  return getMemberType(member, board, implicitAdmin) === 'admin';
};

// Converted from app/scripts/lib/util.js
// Only does standard mode so far; add modes as you need
export const compareMemberTypes = (a: MemberType, b: MemberType) => {
  const hierarchy = [['org'], ['normal'], ['admin']];
  const aTier = hierarchy.findIndex((tier) => tier.includes(a ?? ''));
  const bTier = hierarchy.findIndex((tier) => tier.includes(b ?? ''));
  return aTier - bTier;
};
